import {
  SchemaOf,
  array,
  boolean,
  lazy,
  mixed,
  number,
  object,
  string,
} from 'yup';
import { useMemo } from 'react';
import Lazy from 'yup/lib/Lazy';

import { buildGitValidationSchema } from '@/react/portainer/gitops/GitForm';
import { relativePathValidation } from '@/react/portainer/gitops/RelativePathFieldset/validation';
import { CustomTemplate } from '@/react/portainer/templates/custom-templates/types';
import { TemplateViewModel } from '@/react/portainer/templates/app-templates/view-model';
import {
  DeployMethod,
  GitFormModel,
  RelativePathModel,
} from '@/react/portainer/gitops/types';
import { EnvironmentType } from '@/react/portainer/environments/types';
import { durationValidation } from '@/react/utils/validation';

import { envVarValidation } from '@@/form-components/EnvironmentVariablesFieldset';
import { file } from '@@/form-components/yup-file-validation';

import {
  DeploymentType,
  PORTAINER_EDGE_GROUP_VARIABLE,
  PORTAINER_EDGE_ID_VARIABLE,
} from '../types';
import { staggerConfigValidation } from '../components/StaggerFieldset';
import { defaultPeriodValue } from '../components/RetryDeployPeriod';
import { createHasEnvironmentTypeFunction } from '../ItemView/EditEdgeStackForm/useEdgeGroupHasType';
import { useEdgeGroups } from '../../edge-groups/queries/useEdgeGroups';

import { FormValues, Method } from './types';
import { templateFieldsetValidation } from './TemplateFieldset/validation';
import { useNameValidation } from './NameField';

export function useValidation({
  appTemplate,
  customTemplate,
}: {
  appTemplate: TemplateViewModel | undefined;
  customTemplate: CustomTemplate | undefined;
}): Lazy<SchemaOf<FormValues>> {
  const nameValidation = useNameValidation();
  const edgeGroupsQuery = useEdgeGroups();
  const edgeGroups = edgeGroupsQuery.data;

  return useMemo(
    () =>
      lazy((values: FormValues) =>
        object({
          method: mixed<Method>()
            .oneOf([
              'editor',
              'upload',
              'repository',
              'template',
              'helmRepository',
            ])
            .required(),
          name: nameValidation(values.groupIds),
          groupIds: array(number().required())
            .required()
            .min(1, 'At least one Edge group is required'),
          deploymentType: mixed<DeploymentType>()
            .oneOf([
              DeploymentType.Compose,
              DeploymentType.Kubernetes,
              DeploymentType.KubernetesHelm,
            ])
            .required()
            .test(
              'kubernetes-deployment-type-validation',
              'Kubernetes deployment type is not compatible with the selected edge group(s), which contain Docker environments',
              (value) => {
                if (
                  value !== DeploymentType.Kubernetes &&
                  value !== DeploymentType.KubernetesHelm
                ) {
                  return true;
                }

                const hasType = createHasEnvironmentTypeFunction(
                  values.groupIds,
                  edgeGroups
                );

                const hasDockerEndpoint = hasType(
                  EnvironmentType.EdgeAgentOnDocker
                );

                return !hasDockerEndpoint;
              }
            )
            .test(
              'compose-deployment-type-validation',
              'Compose deployment type is not compatible with the selected edge group(s), which contain Kubernetes environments',
              (value) => {
                if (value !== DeploymentType.Compose) {
                  return true;
                }

                const hasType = createHasEnvironmentTypeFunction(
                  values.groupIds,
                  edgeGroups
                );

                const hasKubeEndpoint = hasType(
                  EnvironmentType.EdgeAgentOnKubernetes
                );

                return !hasKubeEndpoint;
              }
            ),
          envVars: envVarValidation(),
          privateRegistryId: number().default(0),
          prePullImage: boolean().default(false),
          retryDeploy: boolean().default(false),
          retryPeriod: number().default(defaultPeriodValue),
          enableWebhook: boolean().default(false),
          staggerConfig: staggerConfigValidation(),
          fileContent: string()
            .default('')
            .when('method', {
              is: 'editor',
              then: (schema) => schema.required('Config file is required'),
            }),
          file: file().when('method', {
            is: 'upload',
            then: (schema) => schema.required(),
          }),
          templateValues: templateFieldsetValidation({
            customVariablesDefinitions: customTemplate?.Variables || [],
            appTemplateVariablesDefinitions: appTemplate?.Env || [],
          }),
          git: mixed().when('method', {
            is: 'repository',
            then: () => {
              let deploymentMethod: DeployMethod = 'manifest';
              if (values.deploymentType === DeploymentType.Compose) {
                deploymentMethod = 'compose';
              } else if (
                values.deploymentType === DeploymentType.KubernetesHelm
              ) {
                deploymentMethod = 'helm';
              }
              return buildGitValidationSchema(deploymentMethod);
            },
          }) as SchemaOf<GitFormModel>,
          relativePath: mixed().when('method', {
            is: 'repository',
            then: () => relativePathValidation(),
          }) as SchemaOf<RelativePathModel>,
          useManifestNamespaces: boolean().default(false),
          helmChartPath: string()
            .default('')
            .when(['deploymentType', 'method'], {
              is: (deploymentType: DeploymentType, method: string) =>
                deploymentType === DeploymentType.KubernetesHelm &&
                method === 'git',
              then: (schema) => schema.required('Helm chart path is required'),
            }),
          helmValuesFiles: array(
            string().required('Values file path is required')
          )
            .default([])
            .when(['deploymentType'], {
              is: (deploymentType: DeploymentType) =>
                deploymentType === DeploymentType.KubernetesHelm,
              then: (schema) =>
                schema.test(
                  'has-dynamic-variable',
                  function validateDynamicVariable(files) {
                    if (!files || files.length === 0) return true;

                    const hasGroupMatch =
                      !!values.helmRelativePath?.PerDeviceConfigsGroupMatchType;
                    const hasDeviceMatch =
                      !!values.helmRelativePath?.PerDeviceConfigsMatchType;

                    // If neither matching type is configured, no validation needed
                    if (!hasGroupMatch && !hasDeviceMatch) {
                      return true;
                    }

                    const hasDynamicVar = files.some((file) => {
                      if (!file) return false;

                      const hasGroupVar = file.includes(
                        PORTAINER_EDGE_GROUP_VARIABLE
                      );
                      const hasDeviceVar = file.includes(
                        PORTAINER_EDGE_ID_VARIABLE
                      );

                      // Only group matching configured: require group variable
                      if (hasGroupMatch) {
                        return hasGroupVar;
                      }

                      // Only device matching configured: require device variable
                      if (hasDeviceMatch) {
                        return hasDeviceVar;
                      }

                      return false;
                    });

                    if (!hasDynamicVar) {
                      // Build specific error message based on configuration
                      let message = 'At least one values file must use ';
                      if (hasGroupMatch && hasDeviceMatch) {
                        message += `${PORTAINER_EDGE_GROUP_VARIABLE} or ${PORTAINER_EDGE_ID_VARIABLE}`;
                      } else if (hasGroupMatch) {
                        message += `${PORTAINER_EDGE_GROUP_VARIABLE} for per-group configurations`;
                      } else {
                        message += `${PORTAINER_EDGE_ID_VARIABLE} for per-device configurations`;
                      }
                      return this.createError({ message });
                    }

                    return true;
                  }
                ),
            }),
          helmNamespace: string()
            .default('')
            .when('deploymentType', {
              is: DeploymentType.KubernetesHelm,
              then: (schema) =>
                schema.required('Namespace is required for Helm deployment'),
            }),
          helmReleaseName: string()
            .default('')
            .when('deploymentType', {
              is: DeploymentType.KubernetesHelm,
              then: (schema) =>
                schema.required('Release name is required for Helm deployment'),
            }),
          helmAtomic: boolean().default(false),
          helmTimeout: durationValidation(true).default(''), // Allow empty for default timeout
          helmRelativePath: mixed().when('method', {
            is: 'repository',
            then: () => relativePathValidation(),
          }) as SchemaOf<RelativePathModel>,
          helmRepoUrl: string()
            .default('')
            .when(['deploymentType', 'method'], {
              is: (deploymentType: DeploymentType, method: string) =>
                deploymentType === DeploymentType.KubernetesHelm &&
                method === 'helmRepository',
              then: (schema) =>
                schema
                  .required('Helm chart source is required')
                  .url('Helm chart source must be a valid URL'),
            }),
          helmChartName: string()
            .default('')
            .when(['deploymentType', 'method'], {
              is: (deploymentType: DeploymentType, method: string) =>
                deploymentType === DeploymentType.KubernetesHelm &&
                method === 'helmRepository',
              then: (schema) => schema.required('Chart name is required'),
            }),
          helmChartVersion: string().default(''),
          helmValuesInline: string().default(''),
        })
      ),
    [appTemplate?.Env, customTemplate, edgeGroups, nameValidation]
  );
}
